"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";

function sortByEffectiveFromDesc(rows) {
  return [...rows].sort((a, b) => {
    const da = a.effective_from ? String(a.effective_from) : "";
    const db = b.effective_from ? String(b.effective_from) : "";
    return db.localeCompare(da);
  });
}

function recipeVersionId(r) {
  return r?.id ?? r?.version_id ?? null;
}

function isCompleteIngredient(row) {
  const qty = Number(String(row?.qty_per_cum ?? "").trim());
  return Boolean(row?.material_id) && Number.isFinite(qty) && qty > 0;
}

export default function RmcRecipes() {
  const qc = useQueryClient();
  const [partyId, setPartyId] = React.useState(null);
  const [gradeId, setGradeId] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [editingRecipeId, setEditingRecipeId] = React.useState(null);
  const [eff, setEff] = React.useState(new Date().toISOString().slice(0, 10));
  const [ing, setIng] = React.useState([{ material_id: "", qty_per_cum: "", uom: "TON" }]);
  const [dupErr, setDupErr] = React.useState(null);
  const [histOpen, setHistOpen] = React.useState(false);
  const selectedPartyId = partyId != null ? Number(partyId) : null;

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });

  const { data: grades = [] } = useQuery({
    queryKey: ["grades", selectedPartyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: selectedPartyId })).data,
    enabled: selectedPartyId != null,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await rmcClient.listMaterials()).data,
  });
  const { data: versions = [], isFetching } = useQuery({
    queryKey: ["recipes", selectedPartyId, gradeId],
    queryFn: async () =>
      gradeId && selectedPartyId
        ? (await rmcClient.listRecipesForGrade(gradeId, selectedPartyId)).data
        : [],
    enabled: !!gradeId && selectedPartyId != null,
  });

  React.useEffect(() => {
    setGradeId(null);
    setShowNew(false);
    setEditingRecipeId(null);
    setHistOpen(false);
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartyId]);

  const rows = React.useMemo(() => sortByEffectiveFromDesc(versions), [versions]);
  const active = rows[0];
  const history = rows.slice(1);
const uomOptions = [
  { id: "TON", name: "TON" },
  { id: "KG", name: "KG" },
  { id: "LTR", name: "LTR" },
];

  const hasAtLeastOneIngredient = React.useMemo(
    () => ing.some(isCompleteIngredient),
    [ing]
  );

  const resetForm = () => {
    setEff(new Date().toISOString().slice(0, 10));
    setIng([{ material_id: "", qty_per_cum: "", uom: "TON" }]);
    setDupErr(null);
  };

  const openEdit = (recipe) => {
    const id = recipeVersionId(recipe);
    if (id == null) {
      toast.error("This recipe has no version id; cannot edit.");
      return;
    }
    setEditingRecipeId(id);
    setEff(recipe.effective_from?.slice(0, 10) || "");
    setIng(
      (recipe.ingredients || []).length
        ? recipe.ingredients.map((i) => ({
            material_id: String(i.material_id || ""),
            qty_per_cum: String(i.qty_per_cum ?? ""),
            uom: i.uom || "TON",
          }))
        : [{ material_id: "", qty_per_cum: "", uom: "TON" }]
    );
    setDupErr(null);
    setShowNew(true);
  };

  const openNew = () => {
    setEditingRecipeId(null);
    resetForm();
    setShowNew(true);
  };

  const save = useMutation({
    mutationFn: () => {
      if (selectedPartyId == null) {
        throw new Error("party");
      }
      if (!ing.some(isCompleteIngredient)) {
        throw new Error("ingredients");
      }
      const matIds = ing.map((i) => Number(i.material_id)).filter(Boolean);
      if (new Set(matIds).size !== matIds.length) {
        throw new Error("dup");
      }
      const payload = {
        party_id: selectedPartyId,
        effective_from: eff,
        ingredients: ing
          .filter(isCompleteIngredient)
          .map((i) => ({
            material_id: Number(i.material_id),
            qty_per_cum: Number(i.qty_per_cum),
            uom: i.uom,
          })),
      };
      if (editingRecipeId) {
        return rmcClient.updateRecipeVersion(editingRecipeId, payload);
      }
      return rmcClient.addRecipeVersion({ ...payload, grade_id: gradeId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipes", selectedPartyId, gradeId] });
      toast.success(editingRecipeId ? "Recipe updated" : "Recipe saved");
      setShowNew(false);
      setEditingRecipeId(null);
      resetForm();
    },
    onError: (e) => {
      if (e.message === "party") toast.error("Select a sales party first.");
      else if (e.message === "dup") setDupErr("Duplicate material in recipe.");
      else if (e.message === "ingredients") {
        toast.error("Add at least one material with a quantity greater than zero.");
      } else toast.error(handleApiError(e));
    },
  });

  const removeRecipe = useMutation({
    mutationFn: () => {
      if (!gradeId || selectedPartyId == null) {
        throw new Error("select");
      }
      return rmcClient.deleteRecipeVersion(gradeId, selectedPartyId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipes", selectedPartyId, gradeId] });
      toast.success("Recipe deleted");
      setShowNew(false);
      setEditingRecipeId(null);
      resetForm();
    },
    onError: (e) => {
      if (e.message === "select") toast.error("Select party and grade first.");
      else toast.error(handleApiError(e));
    },
  });

  const ingredientTable = (recipe) => (
    <div className="overflow-hidden rounded-lg border border-gray-800 mt-1">
      <table className="min-w-full text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/80">
            <th className="px-3 py-2 font-medium text-gray-500">Material</th>
            <th className="px-3 py-2 font-medium text-gray-500">Qty / CUM</th>
            <th className="px-3 py-2 font-medium text-gray-500">UOM</th>
          </tr>
        </thead>
        <tbody>
          {(recipe?.ingredients || []).map((i, idx) => {
            const m = materials.find((x) => x.id === i.material_id);
            return (
              <tr key={idx} className="border-b border-gray-800/80 last:border-0">
                <td className="px-3 py-2.5 text-gray-200">{m?.name || "—"}</td>
                <td className="px-3 py-2.5 text-gray-300">
                  {Number(i.qty_per_cum).toFixed(2)}
                </td>
                <td className="px-3 py-2.5 text-gray-300">{i.uom}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Mix Design / Recipes" />
      <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
        <SearchableSelect
          label="Sales Party"
          value={partyId}
          onChange={(id) => setPartyId(id ?? null)}
          options={parties}
          getLabel={(p) => p.name}
          getValue={(p) => p.id}
          getSubLabel={(p) => p.gst_number}
          placeholder="Select sales party…"
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-4 rounded-lg border border-gray-800 bg-gray-900/40 p-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Grades</p>
          {selectedPartyId == null ? (
            <p className="px-2 py-1 text-xs text-gray-500">Select a party to view grades.</p>
          ) : grades.length === 0 ? (
            <p className="px-2 py-1 text-xs text-gray-500">No grades available for this party.</p>
          ) : null}
          <ul className="space-y-1">
            {grades.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  className={`w-full rounded-md px-2 py-1.5 text-left text-xs ${
                    gradeId === g.id ? "bg-white text-black" : "text-gray-300 hover:bg-white/5"
                  }`}
                  onClick={() => {
                    setGradeId(g.id);
                    setShowNew(false);
                    setEditingRecipeId(null);
                  }}
                >
                  {g.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-8 space-y-4">
          {!gradeId && (
            <p className="text-sm text-gray-500">
              {selectedPartyId == null
                ? "Select a sales party, then choose a grade."
                : "Select a grade to view recipes."}
            </p>
          )}
          {gradeId && (
            <>
              <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white">Current recipe (newest)</h3>
                    {isFetching && <span className="text-[11px] text-gray-500">Loading…</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        openNew();
                      }}
                    >
                      New recipe version
                    </Button>
                    {active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          openEdit(active);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {active && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={removeRecipe.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Delete current recipe for this grade and party? This action cannot be undone."
                            )
                          ) {
                            removeRecipe.mutate();
                          }
                        }}
                      >
                        {removeRecipe.isPending ? "Deleting…" : "Delete"}
                      </Button>
                    )}
                  </div>
                </div>
                {active ? (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-400">
                      Effective from:{" "}
                      <span className="font-medium text-gray-200">{active.effective_from}</span>
                      {recipeVersionId(active) != null && (
                        <span className="ml-3 font-mono text-gray-500">
                          v{recipeVersionId(active)}
                        </span>
                      )}
                    </p>
                    {ingredientTable(active)}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No recipe yet for this grade.</p>
                )}
              </div>

              {history.length > 0 && (
                <div className="rounded-lg border border-gray-800">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-xs text-gray-400 hover:bg-white/5"
                    onClick={() => setHistOpen(!histOpen)}
                  >
                    Past versions (detail) ({history.length}) {histOpen ? "▼" : "▶"}
                  </button>
                  {histOpen && (
                    <div className="border-t border-gray-800 p-3 text-xs space-y-3">
                      {history.map((v) => (
                        <div
                          key={recipeVersionId(v) ?? v.effective_from}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-800/80 bg-gray-950/40 p-2"
                        >
                          <span className="text-gray-400">
                            From {v.effective_from}
                            {recipeVersionId(v) != null && (
                              <span className="ml-2 font-mono text-gray-500">v{recipeVersionId(v)}</span>
                            )}
                          </span>
                          <Button size="sm" variant="outline" onClick={() => openEdit(v)}>
                            Edit
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showNew && (
                <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4 space-y-3">
                  <p className="text-[11px] text-amber-200/90">
                    {editingRecipeId
                      ? "Updating this recipe version. Effective date controls which challans use this mix."
                      : `New version applies to Sales Challans on or after ${eff}.`}
                  </p>
                  <div>
                    <Label>Effective from</Label>
                    <Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mt-1" />
                  </div>
                  {dupErr && <p className="text-[11px] text-red-400">{dupErr}</p>}
                  {!hasAtLeastOneIngredient && (
                    <p className="text-[11px] text-amber-200/90">
                      Select a material and enter a quantity for at least one line before saving.
                    </p>
                  )}
                  {ing.map((row, idx) => (
                    <div key={idx} className="flex flex-wrap gap-2 items-end">
                      <div className="min-w-[120px] flex-1">
                        <SearchableSelect
                          inputId={`recipe-ing-${idx}-material`}
                          value={row.material_id}
                          onChange={(v) => {
                            const next = [...ing];
                            next[idx].material_id = v ? String(v) : "";
                            setIng(next);
                            setDupErr(null);
                          }}
                          options={materials}
                          getLabel={(m) => m.name}
                          getValue={(m) => String(m.id)}
                          placeholder="Material"
                          menuZClass="z-[100002]"
                          overlayZClass="z-[100001]"
                        />
                      </div>
                      <Input
                        placeholder="Qty / CUM"
                        type="number"
                        step="0.001"
                        value={row.qty_per_cum}
                        onChange={(e) => {
                          const next = [...ing];
                          next[idx].qty_per_cum = e.target.value;
                          setIng(next);
                        }}
                        className="w-28"
                      />
                      <div className="w-24">
                        <SearchableSelect
                          value={row.uom}
                          onChange={(v) => {
                            const next = [...ing];
                            next[idx].uom = v ? String(v) : "TON";
                            setIng(next);
                          }}
                          options={uomOptions}
                          getLabel={(o) => o.name}
                          getValue={(o) => o.id}
                          placeholder="UOM"
                          menuZClass="z-[100002]"
                          overlayZClass="z-[100001]"
                        />
                      </div>
                      <button
                        type="button"
                        className="text-red-400"
                        onClick={() => setIng(ing.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const nextIdx = ing.length;
                      flushSync(() => {
                        setIng((prev) => [
                          ...prev,
                          { material_id: "", qty_per_cum: "", uom: "TON" },
                        ]);
                      });
                      requestAnimationFrame(() => {
                        document.getElementById(`recipe-ing-${nextIdx}-material`)?.focus();
                      });
                    }}
                  >
                    + Add ingredient
                  </Button>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={save.isPending || !hasAtLeastOneIngredient}
                      onClick={() => save.mutate()}
                    >
                      {editingRecipeId ? "Update recipe" : "Save recipe"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowNew(false);
                        setEditingRecipeId(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
